/**
 * Native IndexedDB Wrapper for Offline-First Infrastructure
 *
 * Three object stores:
 * - sync-queue: Buffered mutations (INSERT/UPDATE with timestamp, entity type, payload)
 * - offline-cache: Cached read data (checklists, inspection positions)
 * - offline-images: Base64-encoded images for defect capture
 *
 * @see .lovable/memory/architecture/offline-first-infrastructure.md
 */

import { logger } from "@/lib/logger";

const DB_NAME = "checkmaty-offline";
const DB_VERSION = 1;

const STORES = {
  SYNC_QUEUE: "sync-queue",
  OFFLINE_CACHE: "offline-cache",
  OFFLINE_IMAGES: "offline-images",
} as const;

export interface SyncQueueEntry {
  id?: number; // autoIncrement key
  entityType: "inspection" | "inspection_response" | "defect" | "defect_comment";
  operation: "INSERT" | "UPDATE";
  payload: Record<string, unknown>;
  timestamp: string; // ISO string
  retryCount: number;
  /** Temporary image IDs referenced in payload, to be uploaded during sync */
  imageRefs?: string[];
  /**
   * Set true once the entry exhausted MAX_RETRIES. Dead-lettered entries are KEPT in the
   * queue (not deleted) so the user's queued data is recoverable instead of silently
   * destroyed, and they are skipped by processSyncQueue so they no longer loop. (TES-789)
   */
  failed?: boolean;
  /** The last error that caused the entry to be dead-lettered (for surfacing/recovery). */
  failedReason?: string;
}

export interface CachedData<T = unknown> {
  data: T;
  cachedAt: string; // ISO string
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const store = db.createObjectStore(STORES.SYNC_QUEUE, {
          keyPath: "id",
          autoIncrement: true,
        });
        store.createIndex("entityType", "entityType", { unique: false });
        store.createIndex("timestamp", "timestamp", { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.OFFLINE_CACHE)) {
        db.createObjectStore(STORES.OFFLINE_CACHE);
      }

      if (!db.objectStoreNames.contains(STORES.OFFLINE_IMAGES)) {
        db.createObjectStore(STORES.OFFLINE_IMAGES);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      logger.error("IndexedDB open failed", { db: DB_NAME, error: request.error?.message });
      reject(request.error);
    };
  });
}

function withStore<T>(
  storeName: string,
  mode: IDBTransactionMode,
  callback: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDB().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        const request = callback(store);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => {
          logger.error("IndexedDB request failed", {
            store: storeName,
            mode,
            error: request.error?.message,
          });
          reject(request.error);
        };
        tx.oncomplete = () => db.close();
        tx.onerror = () => {
          logger.error("IndexedDB transaction failed", {
            store: storeName,
            mode,
            error: tx.error?.message,
          });
          db.close();
          reject(tx.error);
        };
      }),
  );
}

// ─── Sync Queue ───────────────────────────────────────────

/** Add a mutation to the sync queue */
export async function enqueue(entry: Omit<SyncQueueEntry, "id">): Promise<number> {
  const id = await withStore<IDBValidKey>(STORES.SYNC_QUEUE, "readwrite", (store) =>
    store.add(entry),
  );
  return id as number;
}

/** Get all entries from the sync queue, ordered by id (FIFO) */
export async function dequeueAll(): Promise<SyncQueueEntry[]> {
  return withStore<SyncQueueEntry[]>(STORES.SYNC_QUEUE, "readonly", (store) => store.getAll());
}

/** Remove a specific entry from the sync queue */
export async function removeFromQueue(id: number): Promise<void> {
  await withStore<undefined>(STORES.SYNC_QUEUE, "readwrite", (store) => store.delete(id));
}

/** Update an entry in the sync queue (e.g., increment retryCount) */
export async function updateQueueEntry(entry: SyncQueueEntry): Promise<void> {
  await withStore<IDBValidKey>(STORES.SYNC_QUEUE, "readwrite", (store) => store.put(entry));
}

/** Single IDB read returning both pending count and dead-letter list (avoids double scan) */
export async function getQueueStats(): Promise<{
  pendingCount: number;
  deadLetters: SyncQueueEntry[];
}> {
  const all = await dequeueAll();
  const deadLetters = all.filter((e) => e.failed === true);
  return { pendingCount: all.length - deadLetters.length, deadLetters };
}

/** Get the number of pending (non-dead-lettered) entries in the sync queue */
export async function getQueueLength(): Promise<number> {
  return (await getQueueStats()).pendingCount;
}

/** Get dead-lettered entries (exhausted MAX_RETRIES) — kept for recovery/surfacing (TES-789) */
export async function getDeadLetterEntries(): Promise<SyncQueueEntry[]> {
  return (await getQueueStats()).deadLetters;
}

// ─── Offline Cache ────────────────────────────────────────

/** Cache data for offline access */
export async function cacheData<T>(key: string, data: T): Promise<void> {
  const cached: CachedData<T> = {
    data,
    cachedAt: new Date().toISOString(),
  };
  await withStore<IDBValidKey>(STORES.OFFLINE_CACHE, "readwrite", (store) =>
    store.put(cached, key),
  );
}

/** Get cached data by key */
export async function getCachedData<T>(key: string): Promise<CachedData<T> | undefined> {
  return withStore<CachedData<T> | undefined>(STORES.OFFLINE_CACHE, "readonly", (store) =>
    store.get(key),
  );
}

// ─── Offline Images ───────────────────────────────────────

/** Store a Base64-encoded image */
export async function storeImage(id: string, base64: string): Promise<void> {
  await withStore<IDBValidKey>(STORES.OFFLINE_IMAGES, "readwrite", (store) =>
    store.put(base64, id),
  );
}

/** Get a stored image by ID */
export async function getImage(id: string): Promise<string | undefined> {
  return withStore<string | undefined>(STORES.OFFLINE_IMAGES, "readonly", (store) => store.get(id));
}

/** Remove a stored image */
export async function removeImage(id: string): Promise<void> {
  await withStore<undefined>(STORES.OFFLINE_IMAGES, "readwrite", (store) => store.delete(id));
}

/** Check if IndexedDB is available */
export function isIndexedDBAvailable(): boolean {
  try {
    return typeof indexedDB !== "undefined";
  } catch {
    return false;
  }
}
